import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';
import { CONTACT_LOG_OUTCOME_TAGS } from '@/clinical-study/vocabulary';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FormFeedback } from '@/components/ui/form-feedback';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { apiClient } from '@/lib/api-client';
import type { paths } from '@/lib/api-types-clinical-study';
import { formatDate } from '@/lib/intl';
import { usePendingWrite } from '@/lib/use-pending-write';

type ContactLogEntry =
  paths['/v1/clinical-study/studies/{studyId}/subjects/{subjectId}/contact-log-entries']['post']['responses'][201]['content']['application/json'];

const contactLogEntrySchema = z.object({
  contactDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'La data deve essere nel formato AAAA-MM-GG.'),
  outcomeTag: z.enum(CONTACT_LOG_OUTCOME_TAGS).optional(),
});
type ContactLogEntryForm = z.infer<typeof contactLogEntrySchema>;

// Resolution R11: a literal `as const` map, never a template-string `t()` key.
const OUTCOME_LABEL_KEY = {
  contacted: 'contactLog.outcomeContacted',
  'no-answer': 'contactLog.outcomeNoAnswer',
  'appointment-set': 'contactLog.outcomeAppointmentSet',
} as const;

/**
 * `ContactLogSection` (FR-9.26): append-only contact-date log entries, an optional closed-
 * vocabulary outcome tag — never free-text (CST-9.5). Resolution R2: no backend list/history
 * endpoint exists, so `entries` is session-scoped React state only, appended in-place from each
 * append call's own response — a page reload loses it, captioned honestly below the form.
 * Resolution R8: the outcome-tag `Select` starts genuinely unselected (no `defaultValues` entry)
 * and offers no sentinel "none" option; the placeholder already communicates "no tag chosen" and
 * the field is simply omitted from the request body when left unselected.
 *
 * Post-execution hardening (2026-07-27, WCAG 2.2 SC 3.3.1/4.1.3, Finding 4): the write-outcome
 * banner (`apiError`) and the `contactDate` client-validation message both render through the
 * shared `FormFeedback` component (`role="alert"`) instead of a plain `<p>`; `contact-log-date`
 * carries `aria-invalid`/`aria-describedby` linking it to that same message when present.
 */
export function ContactLogSection({ studyId, subjectId }: { studyId: string; subjectId: string }) {
  const { t } = useTranslation('clinical-study');
  const [entries, setEntries] = useState<ContactLogEntry[]>([]);
  const [apiError, setApiError] = useState<string | null>(null);

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ContactLogEntryForm>({ resolver: zodResolver(contactLogEntrySchema) });
  const { pending, run } = usePendingWrite();

  const onSubmit = async (values: ContactLogEntryForm) => {
    setApiError(null);
    const { data, error, response } = await apiClient.POST(
      '/v1/clinical-study/studies/{studyId}/subjects/{subjectId}/contact-log-entries',
      {
        params: { path: { studyId, subjectId } },
        // Under this workspace's `exactOptionalPropertyTypes: true`, the generated request DTO's
        // `outcomeTag?: ContactLogOutcomeTag` (optional, NOT nullable) rejects an explicit
        // `outcomeTag: undefined` key — the key must be OMITTED entirely when unselected, which
        // also matches Resolution R8's own runtime contract ("outcomeTag is simply omitted from
        // the request body in that case").
        body: {
          contactDate: values.contactDate,
          ...(values.outcomeTag !== undefined ? { outcomeTag: values.outcomeTag } : {}),
        },
      },
    );
    if (error !== undefined || !response.ok || data === undefined) {
      setApiError(error?.detail ?? t('contactLog.genericError'));
      return;
    }
    reset();
    setEntries((previous) => [data, ...previous]);
    toast.success(t('contactLog.addedToast'));
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('contactLog.title')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <form
          className="space-y-4"
          onSubmit={(event) => void handleSubmit((values) => run(() => onSubmit(values)))(event)}
        >
          <div className="space-y-1">
            <label className="text-sm font-medium" htmlFor="contact-log-date">
              {t('contactLog.dateLabel')}
            </label>
            <Input
              id="contact-log-date"
              type="date"
              aria-invalid={errors.contactDate ? true : undefined}
              aria-describedby={errors.contactDate ? 'contact-log-date-error' : undefined}
              {...register('contactDate')}
            />
            {errors.contactDate && (
              <FormFeedback tone="error" id="contact-log-date-error">
                {errors.contactDate.message}
              </FormFeedback>
            )}
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium" htmlFor="contact-log-outcome">
              {t('contactLog.outcomeLabel')}
            </label>
            <Controller
              control={control}
              name="outcomeTag"
              render={({ field }) => (
                <Select value={field.value ?? ''} onValueChange={field.onChange}>
                  <SelectTrigger id="contact-log-outcome">
                    <SelectValue placeholder={t('contactLog.outcomePlaceholder')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="contacted">{t('contactLog.outcomeContacted')}</SelectItem>
                    <SelectItem value="no-answer">{t('contactLog.outcomeNoAnswer')}</SelectItem>
                    <SelectItem value="appointment-set">
                      {t('contactLog.outcomeAppointmentSet')}
                    </SelectItem>
                  </SelectContent>
                </Select>
              )}
            />
          </div>
          <Button type="submit" disabled={pending}>
            {t('contactLog.submit')}
          </Button>
          {apiError && <FormFeedback tone="error">{apiError}</FormFeedback>}
        </form>
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground">{t('contactLog.sessionListCaption')}</p>
          {entries.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('contactLog.emptyMessage')}</p>
          ) : (
            <ul className="space-y-1 text-sm">
              {entries.map((entry) => (
                <li key={entry.id}>
                  {formatDate(entry.contactDate)}
                  {entry.outcomeTag !== null && ` — ${t(OUTCOME_LABEL_KEY[entry.outcomeTag])}`}
                </li>
              ))}
            </ul>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
